import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import parseISO from 'date-fns/parseISO';
import { fr, enUS, de, es, enGB, it, da, nl, nb, pl, pt, sv } from 'date-fns/locale';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';

const localeMap = {
  [LanguageRegionCode.English]: enUS,
  [LanguageRegionCode.French]: fr,
  [LanguageRegionCode.German]: de,
  [LanguageRegionCode.Spanish]: es,
  [LanguageRegionCode.SpanishLatAm]: es,
  [LanguageRegionCode.EnglishGB]: enGB,
  [LanguageRegionCode.Italian]: it,
  [LanguageRegionCode.Danish]: da,
  [LanguageRegionCode.Dutch]: nl,
  [LanguageRegionCode.Norwegian]: nb,
  [LanguageRegionCode.Polish]: pl,
  [LanguageRegionCode.Portuguese]: pt,
  [LanguageRegionCode.PortugueseBrazilian]: pt,
  [LanguageRegionCode.Swedish]: sv
};

const monthDayYear = (date: Date | string, languageRegionCode: LanguageRegionCode = LanguageRegionCode.English, longMonth = true) => {
  return new Date(date).toLocaleDateString(languageRegionCode, {
    month: longMonth ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formattedDate = (date: Date, timezoneId: string, languageRegionCode: LanguageRegionCode = LanguageRegionCode.English) => {
  if (timezoneId) {
    return date.toLocaleDateString(languageRegionCode, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezoneId,
      timeZoneName: 'short'
    });
  }
  return date.toLocaleDateString(languageRegionCode, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const dateString = (date = new Date(), timezone: null, languageRegionCode: LanguageRegionCode = LanguageRegionCode.English) => {
  return timezone
    ? new Date(date).toLocaleDateString(languageRegionCode, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        timeZoneName: 'short'
      })
    : new Date(date).toLocaleDateString(languageRegionCode, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      });
};

const localizedFormat = (abbreviate = false, locale: LanguageRegionCode = LanguageRegionCode.English, withTime = false) => {
  let result;
  switch (locale) {
    case LanguageRegionCode.English:
      result = abbreviate ? 'MMM. dd, yyyy' : 'MMMM dd, yyyy';
      break;
    case LanguageRegionCode.German:
      result = abbreviate ? 'dd. MMM. yyyy' : 'dd. MMMM yyyy';
      break;
    case LanguageRegionCode.French:
      result = abbreviate ? 'dd MMM yyyy' : 'dd MMMM yyyy';
      break;
    case LanguageRegionCode.Spanish:
      result = abbreviate ? "dd 'de' MMM. 'de' yyyy" : "dd 'de' MMMM 'de' yyyy";
      break;
    default:
      result = abbreviate ? 'MMM dd, yyyy' : 'MMMM dd, yyyy';
  }
  if (withTime) {
    switch (locale) {
      case LanguageRegionCode.English:
        result = `${result} hh:mm a z`;
        break;
      default:
        result = `${result} HH:mm`;
    }
  }
  return result;
};

const isToday = (potentialDate: string | Date) => {
  const date = new Date(potentialDate);
  const today = new Date();

  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() && today.getFullYear();
};

const timeAgo = (dateStr: string, languageRegionCode: LanguageRegionCode) => {
  const date = parseISO(dateStr);
  const options = { addSuffix: true, locale: localeMap[languageRegionCode] };
  return formatDistanceToNow(date, options);
};

const parseTimeString = (time: string) => {
  return parseInt(time.split(':')[0]);
};

const betweenHours = (start: string, end: string) => {
  const [startHour, endHour] = [parseTimeString(start), parseTimeString(end)];
  const currentHour = new Date().getHours();

  // this would be that the time goes from late PM to early AM
  if (startHour >= endHour) {
    return currentHour >= startHour;
  }

  return currentHour >= startHour && currentHour <= endHour;
};

// These allow a user to edit a UTC date/time without it being automatically shown as local time by DatePicker
// Note that the use of getTimezoneOffset must occur on the date to be converted and not the current date to account
// for changes in timezone offset across DST boundaries
const adjustToSameNominalTimeForUser = (value: string | number): Date => {
  const date = new Date(value);
  date.setTime(date.getTime() + 60000 * date.getTimezoneOffset());
  return date;
};
const adjustFromSameNominalTimeForUser = (localDateObject: Date): Date =>
  new Date(localDateObject.getTime() - 60000 * localDateObject.getTimezoneOffset());

export {
  localeMap,
  formattedDate,
  monthDayYear,
  dateString,
  localizedFormat,
  isToday,
  timeAgo,
  betweenHours,
  adjustToSameNominalTimeForUser,
  adjustFromSameNominalTimeForUser
};
